import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import PourAndDispenseInputModal from "./pour_and_dispense_input_modal";
import axios from "axios";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import {
  getCannabisDispenseDisplay,
  getAlcoholDisplayFromTime,
  getEntryFromTime,
  formatMinuteAndHour,
  formatTimeToJSDatObject,
  PossibleTimesToDispense
} from "./pour_and_dispense_utils";
import { TextField } from "@mui/material";
import PourAndDispenseNotesModal from "./pour_and_dispense_notes_modal";

export const dateToString = (date, addMonth) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + (addMonth ? 1 : 0)).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const stringToDate = (value, removeMonth) => {
  const newDateValues = value.split("-");
  return new Date(
    parseInt(newDateValues[0]),
    parseInt(newDateValues[1]) + (removeMonth ? -1 : 0),
    parseInt(newDateValues[2])
  );
};

const PourAndDispenseTable = (props) => {
  const { clients: propsClients } = props;
  const [timeToEdit, setTimeToEdit] = useState(undefined);
  const [clientToEdit, setClientToEdit] = useState(undefined);
  const [clients, setClients] = useState(propsClients);
  const [createdEntry, setCreatedEntry] = useState(false);
  const [search, setSearch] = useState(undefined);
  const [editNote, setEditNote] = useState(false);

  const [clientsToShow, setClientsToShow] = useState([]);

  const hasClientsToShow = useMemo(() => clientsToShow.length !== 0, [clientsToShow]);

  const day = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const date = searchParams.get("date");
    if (!date) return dateToString(new Date(), true);
    return date;
  }, []);

  useEffect(() => {
    if (search === undefined || search.length === 0) {
      setClientsToShow(clients);
      return;
    }
    setClientsToShow(
      clients.filter((client) =>
        (client.first_name + " " + client.last_name).toLowerCase().match(search.toLowerCase())
      )
    );
  }, [search, clients]);

  const resetVariables = () => {
    setTimeToEdit(undefined);
    setClientToEdit(undefined);
    setCreatedEntry(undefined);
  };

  const deleteAlcoholEntry = async () => {
    const pourTime = formatTimeToJSDatObject(timeToEdit, day);
    try {
      const response = await axios.delete(
        "/pour_and_dispense/alcohol_given_time_entry_delete.json",
        {
          data: { client_id: clientToEdit, pour_time: pourTime }
        }
      );
      if (response.status !== 200) throw "";
      setClients(
        clients.map((client) => {
          if (client.id !== clientToEdit) return client;
          return {
            ...client,
            alcohol_given_time_entries: client.alcohol_given_time_entries.filter((entry) => {
              if (new Date(entry.pour_time).getTime() !== pourTime.getTime()) {
                return true;
              }
            })
          };
        })
      );
      resetVariables();
    } catch (err) {
      console.log(err);
      toastr.error("Something went wrong");
      resetVariables();
      return;
    }
    return;
  };

  const onFinishAlcoholInput = async (type, amount, isDeleting) => {
    const pourTime = formatTimeToJSDatObject(timeToEdit, day);
    if (isDeleting) {
      deleteAlcoholEntry(type, amount);
      return;
    }

    if (type === undefined || amount === undefined) {
      resetVariables();
      return;
    }
    try {
      const response = await axios.post("/pour_and_dispense/alcohol_given_time_entry_create.json", {
        client_id: clientToEdit,
        pour_time: pourTime,
        pour_type: type,
        volume: amount
      });
      if (response.status !== 200) throw "";
      // updating local state
      setClients(
        clients.map((client) => {
          if (client.id !== clientToEdit) return client;
          const newOrUpdatedInformation = {
            client_id: clientToEdit,
            pour_time: pourTime,
            pour_type: type,
            volume: amount
          };
          if (
            !client.alcohol_given_time_entries.find(
              (entry) => new Date(entry.pour_time).getTime() === pourTime.getTime()
            )
          ) {
            return {
              ...client,
              alcohol_given_time_entries: [
                ...client.alcohol_given_time_entries,
                newOrUpdatedInformation
              ]
            };
          }
          return {
            ...client,
            alcohol_given_time_entries: client.alcohol_given_time_entries.map((entry) => {
              if (new Date(entry.pour_time).getTime() === pourTime.getTime()) {
                return newOrUpdatedInformation;
              }
              return entry;
            })
          };
        })
      );
      resetVariables();
    } catch (err) {
      console.log(err);
      toastr.error("Something went wrong");
      resetVariables();
    }
  };

  const onCannabisInput = async (time, client_to_edit) => {
    try {
      const dispenseTime = formatTimeToJSDatObject(time, day);
      const response = await axios.post(
        "/pour_and_dispense/cannabis_given_time_entry_create.json",
        {
          client_id: client_to_edit,
          dispense_time: dispenseTime
        }
      );
      if (response.status !== 200) throw "";
      // updating local state
      setClients(
        clients.map((client) => {
          if (client.id !== client_to_edit) return client;
          const newOrUpdatedInformation = {
            client_id: client_to_edit,
            dispense_time: dispenseTime
          };
          if (
            !client.cannabis_dispense_time_entries.find(
              (entry) => new Date(entry.dispense_time).getTime() === dispenseTime.getTime()
            )
          ) {
            return {
              ...client,
              cannabis_dispense_time_entries: [
                ...client.cannabis_dispense_time_entries,
                newOrUpdatedInformation
              ]
            };
          }
          return {
            ...client,
            cannabis_dispense_time_entries: client.cannabis_dispense_time_entries.filter(
              (entry) => {
                if (new Date(entry.dispense_time).getTime() !== dispenseTime.getTime()) {
                  return true;
                }
              }
            )
          };
        })
      );
      resetVariables();
    } catch (err) {
      console.log(err);
      toastr.error("Something went wrong");
      resetVariables();
    }
  };

  const onFinishEditingNotes = async (newNoteState) => {
    try {
      if (newNoteState === undefined) {
        setClientToEdit();
        setEditNote(false);
        return;
      }
      const response = await axios.post("/pour_and_dispense/pour_and_dispense_edit_notes.json", {
        client_id: clientToEdit,
        pour_and_dispense_note: newNoteState
      });
      if (response.status !== 200) throw "";
      setClientToEdit();
      setEditNote(false);
      setClients(
        clients.map((client) => {
          if (client.id !== clientToEdit) return client;
          return {
            ...client,
            pour_and_dispense_note: newNoteState
          };
        })
      );
    } catch (err) {
      console.log(err);
      toastr.error("Something went wrong saving the note.");
    }
  };

  const editNoteFor = (client) => {
    setClientToEdit(client.id);
    setEditNote(client.pour_and_dispense_note ? client.pour_and_dispense_note : true);
  };

  const onDateChange = (event) => {
    const newUrl = `${window.location.origin}${window.location.pathname}?date=${event.target.value}`;
    window.location.href = newUrl;
  };

  const onDateDayChange = (previous) => {
    const currentDayDate = new Date(stringToDate(day, true));
    const newDate = new Date(
      currentDayDate.setDate(currentDayDate.getDate() + (previous ? -1 : 1))
    ); // Add the days to the current date
    const newUrl = `${window.location.origin}${window.location.pathname}?date=${dateToString(
      newDate,
      true
    )}`;
    window.location.href = newUrl;
  };

  return (
    <div className="table-container">
      <div className="pour-and-dispense-table-inputs ccw-panel">
        <div className="pour-and-dispense-search-field">
          <TextField
            className="pour-and-dispense-search-input"
            id="pour-and-dispense-client-search"
            label="Find client"
            placeholder="Search by client name"
            size="small"
            value={search || ""}
            variant="outlined"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className="pour-and-dispense-previous-and-next-day-buttons-container">
          <button
            type="button"
            aria-label="Show previous day"
            className="pour-and-dispense-change-day-buttons previous-day-button ccw-button"
            onClick={() => onDateDayChange(true)}
          >
            <span aria-hidden="true">‹</span> Previous day
          </button>
          <label className="pour-and-dispense-day-field" htmlFor="pour-and-dispense-day-picker">
            <span>Date</span>
            <input
              id="pour-and-dispense-day-picker"
              value={day}
              onChange={onDateChange}
              type="date"
              className="pour-and-dispense-day-picker ccw-input"
            />
          </label>
          <button
            type="button"
            aria-label="Show next day"
            onClick={() => onDateDayChange(false)}
            className="pour-and-dispense-change-day-buttons next-day-button ccw-button"
          >
            Next day <span aria-hidden="true">›</span>
          </button>
        </div>
      </div>
      <PourAndDispenseNotesModal
        isOpen={editNote}
        onComplete={onFinishEditingNotes}
        currentValue={editNote === true ? "" : editNote}
      />
      <PourAndDispenseInputModal
        isOpen={!!timeToEdit}
        time={formatMinuteAndHour(timeToEdit)}
        onComplete={onFinishAlcoholInput}
        canDelete={!!createdEntry}
      />
      {!hasClientsToShow ? (
        <div className="ccw-surface ccw-empty-state pour_and_dispense_empty_state">
          <strong>No clients found</strong>
          <span>Try a different name or clear the search field.</span>
        </div>
      ) : (
        <>
          <p className="pour-and-dispense-ledger-hint">Scroll sideways for later times.</p>
          <div
            className="pour-and-dispense-ledger-scroll"
            role="region"
            aria-label="Client pour and dispense ledger"
            tabIndex="0"
          >
            <table className="pour-and-dispense-ledger">
              <thead>
                <tr>
                  <th scope="col" className="pour-and-dispense-ledger__client">
                    Client
                  </th>
                  <th scope="col" className="pour-and-dispense-ledger__note">
                    Note
                  </th>
                  {PossibleTimesToDispense.map((time) => (
                    <th scope="col" key={`${time.hour}-${time.minutes}`}>
                      <time dateTime={formatMinuteAndHour(time)}>{formatMinuteAndHour(time)}</time>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clientsToShow.map((row) => {
                  const clientName = `${row.first_name} ${row.last_name}`;

                  return (
                    <tr key={row.id}>
                      <th scope="row" className="pour-and-dispense-ledger__client">
                        {clientName}
                        <button
                          type="button"
                          className="pour-and-dispense-mobile-note-button"
                          aria-label={`Edit note for ${clientName}`}
                          title={row.pour_and_dispense_note || "Add note"}
                          onClick={() => editNoteFor(row)}
                        >
                          {row.pour_and_dispense_note || "Add note"}
                        </button>
                      </th>
                      <td className="pour-and-dispense-ledger__note">
                        <button
                          type="button"
                          className="pour-and-dispense-note-button"
                          aria-label={`Edit note for ${clientName}`}
                          title={row.pour_and_dispense_note || "Add note"}
                          onClick={() => editNoteFor(row)}
                        >
                          {row.pour_and_dispense_note || "Add note"}
                        </button>
                      </td>
                      {PossibleTimesToDispense.map((time) => {
                        const timeLabel = formatMinuteAndHour(time);
                        const alcoholEntry = getEntryFromTime(
                          time,
                          true,
                          row.alcohol_given_time_entries
                        );
                        const cannabisEntry = getEntryFromTime(
                          time,
                          false,
                          row.cannabis_dispense_time_entries
                        );
                        const alcoholDisplay =
                          getAlcoholDisplayFromTime(time, row.alcohol_given_time_entries) || "—";
                        let cannabisDisplay = "No";
                        if (time.disableCannabis) cannabisDisplay = "Not scheduled";
                        else if (
                          getCannabisDispenseDisplay(time, row.cannabis_dispense_time_entries) ===
                          "YES"
                        )
                          cannabisDisplay = "Yes";

                        return (
                          <td key={`${row.id}-${time.hour}-${time.minutes}`}>
                            <div className="alcohol_cannabis_input_container">
                              <button
                                type="button"
                                aria-label={`${clientName}, ${timeLabel}, alcohol: ${
                                  alcoholEntry ? alcoholDisplay : "not recorded"
                                }`}
                                onClick={() => {
                                  setTimeToEdit(time);
                                  setClientToEdit(row.id);
                                  setCreatedEntry(!!alcoholEntry);
                                }}
                                className={`alcohol_cannabis_button_base_style alcohol_button_style ${
                                  alcoholEntry ? "is-recorded" : ""
                                }`}
                              >
                                {alcoholDisplay}
                              </button>
                              <button
                                type="button"
                                aria-label={`${clientName}, ${timeLabel}, cannabis: ${cannabisDisplay}`}
                                className={`alcohol_cannabis_button_base_style cannabis_button_style ${
                                  cannabisEntry ? "is-recorded" : ""
                                }`}
                                disabled={time.disableCannabis}
                                onClick={() => onCannabisInput(time, row.id)}
                              >
                                {time.disableCannabis ? "—" : cannabisDisplay}
                              </button>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
PourAndDispenseTable.propTypes = {
  clients: PropTypes.array.isRequired
};

export default PourAndDispenseTable;
